import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useMemo } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { buildKardaMuiTheme } from "./theme/buildKardaMuiTheme";
import { queryClient } from "./shared/api/queryClient";
import { ToastProvider } from "./shared/toast";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";

import LoginPage from "./pages/Login";
import ProfilePage from "./pages/Profile";
import HomePage from "./pages/Home";
import VideoSelectionPage from "./pages/VideoSelection";
import { TranscriptsListPage, TranscriptViewPage } from "./pages/Transcripts";
import GenerateReportPage from "./pages/GenerateReport";
import GeneratingPage from "./pages/Generating";
import EditSectionPage from "./pages/EditSection";
import ConfirmationPage from "./pages/Confirmation";
import NotFound404 from "./pages/NotFound";
import { ExtractedInfoPage } from "./pages/ExtractedInfo";
import { FinalEditsPage } from "./pages/FinalEdits";
import ReviewPlanPage from "./pages/ReviewPlan";
import { ReviewPlanRouteGuard } from "./pages/ReviewPlan/ReviewPlanRouteGuard";
import ManageUsersPage from "./pages/Admin/ManageUsers";
import { ProtectedView } from "./components/layout/ProtectedView";
import { ConnectBodycam } from "./pages/ConnectBodycam";
import { AnalyticsPage } from "./pages/Analytics";
import { AuthTokenBridge } from "./components/auth/AuthTokenBridge";
import { AuthCallback } from "./pages/AuthCallback";
import { InitiateLogin } from "./pages/InitiateLogin";
import { RequirePermission } from "./components/auth/RequirePermission";
import { RequireCoApproval } from "./components/auth/RequireCoApproval";
import {
  USERS_ADMIN_PERMISSION,
  REPORTS_MANAGE_STATION_PERMISSION,
  EVIDENCE_EDIT_OWN_PERMISSION,
  EVIDENCE_EDIT_STATION_PERMISSION,
} from "./shared/auth/authorization";
import CoReviewQueuePage from "./pages/CoReview/CoReviewQueuePage";
import CoReviewPage from "./pages/CoReview/CoReviewPage";
import { ColorModeProvider, useColorMode } from "./context/ColorModeContext";
import { OrgIdProvider } from "./shared/auth/orgContext";
import { RequireRole } from "./components/auth/RequireRole";
import {
  KardaAdminPage,
  KardaStationDetailPage,
  StationDetailsSection,
  StationUsersSection,
  StationBodycamSection,
  StationSettingsSection,
  UserMappingsSection,
  KardaAdminProvider,
} from "./pages/KardaAdmin";
import KardaAdminLayout from "./pages/KardaAdmin/KardaAdminLayout";
import TemplateUploadPage from "./pages/TemplateUpload/TemplateUploadPage";
import {
  StationAdminLayout,
  StationAdminIndex,
  StationAdminInternalAffairs,
} from "./pages/StationAdmin";
import { STATION_ADMIN_ROLE } from "./shared/auth/authorization";
import { RequireStationAdminArea } from "./components/auth/RequireStationAdminArea";
import { RequireInternalAffairs } from "./components/auth/RequireInternalAffairs";

function AppContent() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildKardaMuiTheme(mode), [mode]);

  const protectedPages = (
    <>
      <Route path="/" element={<HomePage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/video-selection" element={<VideoSelectionPage />} />
      <Route
        path="/transcripts"
        element={
          <RequirePermission
            permission={[
              EVIDENCE_EDIT_OWN_PERMISSION,
              EVIDENCE_EDIT_STATION_PERMISSION,
            ]}
          >
            <TranscriptsListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/transcripts/:evidenceId"
        element={
          <RequirePermission
            permission={[
              EVIDENCE_EDIT_OWN_PERMISSION,
              EVIDENCE_EDIT_STATION_PERMISSION,
            ]}
          >
            <TranscriptViewPage />
          </RequirePermission>
        }
      />
      <Route
        path="/generate-report/:evidenceID"
        element={<GenerateReportPage />}
      />
      <Route path="/generating/:jobId" element={<GeneratingPage />} />
      <Route
        path="/preview-report-info/:reportID"
        element={<ExtractedInfoPage />}
      />
      <Route path="/edit-section/:reportID" element={<EditSectionPage />} />
      <Route
        path="/review-plan/:reportID?"
        element={
          <ReviewPlanRouteGuard>
            <ReviewPlanPage />
          </ReviewPlanRouteGuard>
        }
      />
      <Route path="/final-edits/:reportID" element={<FinalEditsPage />} />
      {/* Param optional: pre-rename bookmarks of the bare /confirmation sign
          page get the page's clean home redirect instead of the 404. */}
      <Route
        path="/confirmation/:reportID?"
        element={<ConfirmationPage />}
      />
      <Route
        path="/co-review"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewQueuePage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/co-review/:reportID"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewPage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/manage-users"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ManageUsersPage />
          </RequirePermission>
        }
      />
      <Route
        path="/karda-admin"
        element={
          <RequireRole roleSlug="org-karda-admin">
            <KardaAdminProvider />
          </RequireRole>
        }
      >
        <Route element={<KardaAdminLayout />}>
          <Route index element={<KardaAdminPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="templates" element={<TemplateUploadPage />} />
          <Route
            path="stations/:stationId"
            element={<KardaStationDetailPage />}
          >
            <Route index element={<StationDetailsSection />} />
            <Route path="users" element={<StationUsersSection />} />
            <Route path="bodycam" element={<StationBodycamSection />} />
            <Route path="mappings" element={<UserMappingsSection />} />
            <Route path="settings" element={<StationSettingsSection />} />
          </Route>
        </Route>
      </Route>
      {/* Station Admin is reachable when EITHER per-station flag is on —
          `admin-dashboard` (Overview) or `internal-affairs` (IA tab) — via
          RequireStationAdminArea (404 only when both are off), then gated by
          the station_admin role. Each destination keeps its own flag: the
          index resolves to the Overview dashboard when `admin-dashboard` is on
          and otherwise redirects to the IA tab; the IA route is wrapped in
          RequireInternalAffairs. Flag guard outside role guard so a fully
          disabled station sees "this feature does not exist" regardless of
          role. */}
      <Route
        path="/station-admin"
        element={
          <RequireStationAdminArea>
            <RequireRole roleSlug={STATION_ADMIN_ROLE}>
              <StationAdminLayout />
            </RequireRole>
          </RequireStationAdminArea>
        }
      >
        <Route index element={<StationAdminIndex />} />
        {/* Internal Affairs is gated a SECOND time, by its own per-station
            `internal-affairs` flag (RequireInternalAffairs), so a station can
            run the dashboard with the IA tab on or off independently of
            `admin-dashboard`. The nav tab hides itself the same way. */}
        <Route
          path="internal-affairs"
          element={
            <RequireInternalAffairs>
              <StationAdminInternalAffairs />
            </RequireInternalAffairs>
          }
        />
      </Route>
      <Route
        path="/connect-bodycam"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ConnectBodycam />
          </RequirePermission>
        }
      />
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <Router>
        <AuthTokenBridge />
        <Routes>
          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <LoginPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/initiate-login"
            element={
              <RouteErrorBoundary>
                <InitiateLogin />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/callback"
            element={
              <RouteErrorBoundary>
                <AuthCallback />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedView>
                <RouteErrorBoundary>
                  <Outlet />
                </RouteErrorBoundary>
              </ProtectedView>
            }
          >
            {protectedPages}
          </Route>
          <Route
            path="*"
            element={
              <RouteErrorBoundary>
                <NotFound404 />
              </RouteErrorBoundary>
            }
          />
        </Routes>
      </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary variant="bare">
      {/* Above ColorModeProvider/OrgIdProvider: the query layer has no
          theme or auth dependency, and the module-singleton client must sit
          over everything that will query. */}
      <QueryClientProvider client={queryClient}>
        <ColorModeProvider>
          <OrgIdProvider>
            <AppContent />
          </OrgIdProvider>
        </ColorModeProvider>
        {/* Compiles to a no-op in production builds. */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
